import React, { useState } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useDispatch } from 'react-redux';
import FileUpload from '@/components/FileUpload';
import { showToast } from '@/features/toast/toastSlice';
import { useDownloadUserImportTemplateMutation, useReadUserImportMutation } from '../api/userImportsApi';

/**
 * The user import page's first step: download the template, and choose a filled-in .xlsx,
 * which is read straight away (readUserImport; FileUpload shows it's busy meanwhile). A
 * file the API refuses (e.g. a missing required column) shows its reason here, and choosing
 * another tries again; once it's read, onRead({ storagePath, fileName, rows }) and the page
 * shows its rows to review (UserImportReview).
 */
export const UserImportUpload = ({ onRead }) => {
	const dispatch = useDispatch();
	const [files, setFiles] = useState([]);
	const [downloadTemplate, { isLoading: isDownloading }] =
		useDownloadUserImportTemplateMutation();
	const [readUserImport, { isLoading: isReading, error }] = useReadUserImportMutation();

	const handleDownload = async () => {
		const result = await downloadTemplate();
		if (result.error) {
			dispatch(
				showToast({ message: 'Couldn\'t download the template. Please try again.', error: true }),
			);
		}
	};

	// Choosing a file reads it straight away; removing it reads nothing
	const handleFilesChange = async (newFiles) => {
		setFiles(newFiles);
		if (!newFiles.length) return;
		const result = await readUserImport(newFiles[0]);
		if (result.data) {
			onRead(result.data);
		}
	};

	return (
		<Stack spacing={2}>
			<Typography variant='form_label'>Template</Typography>
			<Button
				variant='outlined'
				startIcon={isDownloading ? <CircularProgress size={18} /> : <DownloadIcon />}
				onClick={handleDownload}
				disabled={isDownloading}
				sx={{ alignSelf: 'flex-start' }}
			>
				Download template
			</Button>
			<Typography variant='form_label'>Upload</Typography>
			<FileUpload
				files={files}
				setFiles={handleFilesChange}
				loading={isReading}
				types={['xlsx']}
				multiple={false}
			/>
			{error && (
				<Alert severity='error'>
					{error.data?.error ?? 'Couldn\'t upload the file. Please try again.'}
				</Alert>
			)}
		</Stack>
	);
};

UserImportUpload.displayName = 'UserImportUpload';
